<template>
  <div class="dashboard-container">
    <!-- 顶部标题栏 -->
    <div class="header">
      <div class="header-left">
        <button class="logout-btn" @click="handleLogout" title="退出登录">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16,17 21,12 16,7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
        </button>
        <div class="project-total">托管项目总数量：{{ elevatorStats.total }}</div>
        <div class="search-dropdown" v-click-outside="closeDropdown">
          <input
            type="text"
            v-model="searchKeyword"
            placeholder="搜索项目..."
            class="search-input"
            @focus="showDropdown = true"
            @input="filterOptions"
          />
          <span class="dropdown-icon">▼</span>
          <div class="dropdown-list" v-show="showDropdown && filteredOptions.length > 0">
            <div
              class="dropdown-item"
              v-for="item in filteredOptions"
              :key="item.id"
              @click="selectOption(item)"
            >
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>

      <div class="title">海康智慧电梯托管监测平台</div>

      <div class="header-right">
        <!-- 时间检索组件 -->
        <div class="time-filter">
          <div class="filter-label">数据时间：</div>
          <div class="date-range-container">
            <input
              type="date"
              v-model="startDate"
              @change="onDateRangeChange"
              class="date-picker"
            />
            <span class="date-separator">至</span>
            <input
              type="date"
              v-model="endDate"
              @change="onDateRangeChange"
              class="date-picker"
            />
          </div>
        </div>
        <div class="current-time">北京时间：{{ currentTime }}</div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左侧区域 -->
      <div class="left-section">
        <!-- 统计数据和饼图 -->
        <div class="stats-pie-container">
          <DailyEventAnalysis
            :chart-data="homeEventChartData"
            :events="homeEvents"
            :year-stats="homeEventYearStats"
            :stat-labels="homeEventStatLabels"
            enable-project-search
          />
        </div>

        <!-- 中心区域项目数量统计 -->
        <ProjectStatisticsBarChart :chart-data="barChartData" />
      </div>

      <!-- 中间3D电梯模型 -->
      <div class="center-section">
        <ElevatorModel3D :inspection-points="inspectionAreaSiteData" />
      </div>

    <!-- 右侧区域 -->
    <div class="right-section">
      <!-- 电梯维保单位数量统计 -->
      <MaintenanceUnitStats
        :chart-data="barHorizontalChartData"
        :overdue-audit-data="overdueAuditData"
        :current-audit-data="currentAuditData"
        :surpass-wb-data="surpassWbData"
        :experiment125-data="experiment125Data"
        :unfinished-order-data="unfinishedOrderData"
      />
    </div>
    </div>

    <!-- 底部区域 -->
    <div class="bottom-section">
      <!-- 每月自主缴费记录统计 -->
      <PaymentRecordsChart
        :chart-data="lineChartData"
        :order-data="orderChartData"
        :payment-detail-list="feeDetailList"
        :order-detail-list="orderDetailList"
        :default-type="defaultChartType"
        @chart-type-change="handleChartTypeChange"
      />

      <!-- 报警记录 -->
      <AlarmRecords :records="alarmRecords" />

      <!-- 平台按钮 -->
      <div class="platform-buttons">
        <div class="platform-title">快捷访问</div>
        <div class="button-grid">
          <router-link to="/daily-control" class="platform-button">日管控</router-link>
          <router-link to="/weekly-inspection" class="platform-button">周排查</router-link>
          <router-link to="/monthly-inspection" class="platform-button">月调度</router-link>
          <!-- <a href="https://www.haikang.ltd/" target="_blank" class="platform-button"
            >海康智慧官网</a
          > -->
          <a href="https://elevator.bivale.cn" target="_blank" class="platform-button"
            >日立遥监平台</a
          >
          <a href="https://portal.qiniu.com/home" target="_blank" class="platform-button"
            >实时视频平台</a
          >
          <a
            href="https://www.wacloud.net/#/home/index?ref=addtabs"
            target="_blank"
            class="platform-button"
            >梯控管理平台</a
          >
          <a
            href="https://fawu.qizongyun.com/enterpriseLoginNew"
            target="_blank"
            class="platform-button"
            >合同管理平台</a
          >
          <a href="https://www.gzsei.com/#/" target="_blank" class="platform-button"
            >市检服务平台</a
          >
          <a
            href="https://amr.gd.gov.cn/tzsbout/qyfw/#/home"
            target="_blank"
            class="platform-button"
            >设备监管平台</a
          >
          <a
            href="http://iot.loktong.com/login?redirect=%2Findex"
            target="_blank"
            class="platform-button"
            >物联管理平台</a
          >
          <a href="https://mp.weixin.qq.com/" target="_blank" class="platform-button"
            >微信公众平台</a
          >
          <a
            href="https://www.400pt.net/?ref=addtabs"
            target="_blank"
            class="platform-button"
            >热线号码平台</a
          >
          <a href="https://pay.weixin.qq.com/" target="_blank" class="platform-button"
            >微信支付平台</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { dashboardAPI, extractIndexResponse, normalizeIndexPayload } from "@/api/dashboard";
import { logout } from "@/api/login";
import {
  buildHomeEventChartDataFromGroup,
  normalizeHomeEventRecords
} from "@/config/dailyEventConfig";
import {
  buildHomeFeeChartData,
  buildHomeOrderChartData,
  extractHomeFeeRecords,
  extractHomeOrderRecords
} from "@/config/homeChartConfig";
import AlarmRecords from "@/components/AlarmRecords.vue";
import DailyEventAnalysis from "@/components/DailyEventAnalysis.vue";
import ElevatorModel3D from "@/components/ElevatorModel3D.vue";
import PaymentRecordsChart from "@/components/PaymentRecordsChart.vue";
import ProjectStatisticsBarChart from "@/components/ProjectStatisticsBarChart.vue";
import MaintenanceUnitStats from "@/components/MaintenanceUnitStats.vue";

function formatDateValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getYearStartDate() {
  return `${new Date().getFullYear()}-01-01`;
}

export default {
  name: "HomeView",
  components: {
    AlarmRecords,
    DailyEventAnalysis,
    ElevatorModel3D,
    PaymentRecordsChart,
    ProjectStatisticsBarChart,
    MaintenanceUnitStats,
  },
    data() {
      return {
        currentTime: "",
        timer: null,
        dataRefreshTimer: null,
        jumpTimer: null,
      // 下拉搜索相关
      searchKeyword: "",
      showDropdown: false,
      filteredOptions: [],
      // 时间检索相关
      startDate: getYearStartDate(),
      endDate: formatDateValue(new Date()),
      // 所有数据将通过API请求获取
      elevatorStats: {
        total: 0,
        thisYearMaintenance: 0,
        lastYearMaintenance: 0,
        totalEvents: 0,
      },
      homeEventChartData: [],
      homeEvents: [],
      barChartData: [],
      lineChartData: [],
      orderChartData: [],
      feeDetailList: [],
      orderDetailList: [],
      barHorizontalChartData: [],
      overdueAuditData: [],
      currentAuditData: [],
      surpassWbData: [],
      unfinishedOrderData: [],
      experiment125Data: [],
      alarmRecords: [],
      platformButtons: [],
      defaultChartType: "payment", // 默认显示缴费统计
      // 项目列表数据（下拉框数据源）
      communityData: [],
      // 地图点数据（从首页接口获取）
      inspectionAreaSiteData: [],
      // API原始数据
      apiData: null,
      // 首页分模块请求序号，用于忽略过期响应
      homeFetchId: 0,
      // 事件列表数据
      eventList: {},
    };
  },
  computed: {
    homeEventYearStats() {
      return {
        thisYear: this.elevatorStats.thisYearMaintenance,
        lastYear: this.elevatorStats.lastYearMaintenance,
        total: this.elevatorStats.totalEvents
      };
    },
    homeEventStatLabels() {
      return {
        first: "今年触发事件数量",
        second: "上年触发事件数量",
        third: "总触发事件数量"
      };
    }
  },
  directives: {
    "click-outside": {
      bind(el, binding) {
        el._clickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener("click", el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener("click", el._clickOutside);
      },
    },
  },
  created() {
    this.startClock();
  },
  mounted() {
    this.fetchAllData();
    this.startDataRefresh();
  },
    beforeDestroy() {
      if (this.timer) {
        clearInterval(this.timer);
      }
      if (this.dataRefreshTimer) {
        clearInterval(this.dataRefreshTimer);
      }
      // 停止事件轮询
      this.stopEventPolling();
      // 清除倒计时跳转定时器
      if (this.jumpTimer) {
        clearTimeout(this.jumpTimer);
      }
      // 关闭所有消息弹窗
      if (this.$message) {
        this.$message.closeAll();
      }
    },
  methods: {
    // 过滤下拉选项
    filterOptions() {
      const keyword = this.searchKeyword.toLowerCase();
      if (!keyword) {
        this.filteredOptions = [...this.communityData];
      } else {
        this.filteredOptions = this.communityData.filter((item) =>
          item.name.toLowerCase().includes(keyword)
        );
      }
    },

    // 选择下拉选项
    selectOption(item) {
      this.searchKeyword = item.name;
      this.showDropdown = false;
      this.$router.push({
        name: "homeTwo",
        params: { id: item.id },
        query: { name: item.name },
      });
    },

    // 关闭下拉框
    closeDropdown() {
      this.showDropdown = false;
    },

    // 构建首页公共查询参数
    getIndexParams() {
      return {
        starttime: this.startDate ? `${this.startDate} 00:00:00` : "",
        endtime: this.endDate ? `${this.endDate} 23:59:59` : "",
      };
    },

    // 独立请求单个首页模块
    fetchIndexModule(apiMethod, applyMethod, options = {}) {
      const fetchId = this.homeFetchId;
      const { arrayField, label = "首页模块" } = options;
      const params = this.getIndexParams();

      return apiMethod(params)
        .then((response) => {
          if (fetchId !== this.homeFetchId) return;
          const payload = normalizeIndexPayload(
            extractIndexResponse(response),
            arrayField
          );
          applyMethod.call(this, payload);
        })
        .catch((error) => {
          if (fetchId !== this.homeFetchId) return;
          console.error(`${label}加载失败:`, error);
        });
    },

    // 各模块独立拉取，谁先返回谁先渲染
    fetchAllData() {
      this.homeFetchId += 1;

      this.fetchIndexModule(
        dashboardAPI.getIndexSummary,
        this.applySummaryData,
        { label: "概要统计" }
      );
      this.fetchIndexModule(
        dashboardAPI.getIndexEvents,
        this.applyEventsData,
        { label: "事件数据" }
      );
      this.fetchIndexModule(
        dashboardAPI.getIndexFees,
        this.applyFeesData,
        { arrayField: "fee_data", label: "缴费数据" }
      );
      this.fetchIndexModule(
        dashboardAPI.getIndexOrders,
        this.applyOrdersData,
        { arrayField: "order_data", label: "工单数据" }
      );
      this.fetchIndexModule(
        dashboardAPI.getIndexMap,
        this.applyMapData,
        { label: "地图数据" }
      );
      this.fetchIndexModule(
        dashboardAPI.getIndexAudit,
        this.applyAuditData,
        { label: "审验维保" }
      );

      this.fetchEventList();
    },

    applySummaryData(data) {
      if (!data || typeof data !== "object") return;

      this.elevatorStats = {
        ...this.elevatorStats,
        thisYearMaintenance: data.year_event_total || 0,
        lastYearMaintenance: data.last_year_event_total || 0,
        totalEvents: data.event_total || 0,
      };

      if (Array.isArray(data.community_area)) {
        this.barChartData = data.community_area.map((item) => ({
          name: item.name || "",
          value: item.num || 0,
        }));
        this.elevatorStats.total = this.barChartData.reduce(
          (sum, item) => sum + item.value,
          0
        );
      }
    },

    applyMapData(data) {
      if (!data || typeof data !== "object") return;

      if (Array.isArray(data.inspection_area_site)) {
        this.inspectionAreaSiteData = data.inspection_area_site;
      } else {
        this.inspectionAreaSiteData = [];
      }

      if (Array.isArray(data.community_data)) {
        this.communityData = data.community_data.map((item) => ({
          id: item.id,
          name: item.name || "",
        }));
        this.filteredOptions = [...this.communityData];
        localStorage.setItem("communityData", JSON.stringify(this.communityData));
      }
    },

    applyFeesData(data) {
      if (!data || typeof data !== "object") return;

      this.feeDetailList = extractHomeFeeRecords(data);
      if (Array.isArray(data.fee_data)) {
        this.lineChartData = buildHomeFeeChartData(
          data.fee_data,
          this.feeDetailList
        );
      } else {
        this.lineChartData = [];
      }
    },

    applyOrdersData(data) {
      if (!data || typeof data !== "object") return;

      this.orderDetailList = extractHomeOrderRecords(data);
      if (Array.isArray(data.order_data)) {
        this.orderChartData = buildHomeOrderChartData(
          data.order_data,
          this.orderDetailList
        );
      } else {
        this.orderChartData = [];
      }
    },

    applyAuditData(data) {
      if (!data || typeof data !== "object") return;

      if (Array.isArray(data.wb_unit)) {
        this.barHorizontalChartData = data.wb_unit
          .filter((item) => item.wb_name && item.wb_name !== "-")
          .map((item) => ({
            name: item.wb_name || "",
            value: item.num || 0,
          }))
          .sort((a, b) => b.value - a.value);
      }

      if (Array.isArray(data.year_audit)) {
        this.overdueAuditData = data.year_audit.map((item) => ({
          name: item.name || "",
          device_code: item.device_code || "",
          audittimes: item.audittimes || "",
          inspection_organization: item.inspection_organization || "",
        }));
      }

      if (Array.isArray(data.month_audit)) {
        this.currentAuditData = data.month_audit.map((item) => ({
          name: item.name || "",
          device_code: item.device_code || "",
          audittimes: item.audittimes || "",
          inspection_organization: item.inspection_organization || "",
        }));
      }

      if (Array.isArray(data.surpass_wb)) {
        this.surpassWbData = data.surpass_wb.map((item) => ({
          name: item.name || "",
          device_code: item.device_code || "",
          latestMaintenanceDate: item.latestMaintenanceDate || "",
        }));
      }

      if (Array.isArray(data.no_finish_order)) {
        this.unfinishedOrderData = data.no_finish_order.map((item) => ({
          area_name: item.area_name || "",
          level: item.level || "",
          order_type: item.order_type || "",
          repair_staff_name: item.repair_staff_name || "",
        }));
      }

      if (Array.isArray(data.shiyan_125)) {
        this.experiment125Data = data.shiyan_125.map((item) => ({
          name: item.name || "",
          device_code: item.device_code || "",
          audittimes: item.audittimes || "",
          inspection_organization: item.inspection_organization || "",
        }));
      }
    },

    applyEventsData(data) {
      if (!data || typeof data !== "object") return;

      const flatEvents = Array.isArray(data.event_data)
        ? normalizeHomeEventRecords(data.event_data)
        : [];

      if (flatEvents.length) {
        this.homeEvents = flatEvents;
        this.alarmRecords = data.event_data.map((item) => ({
          createtime: item.createtime || "",
          event_name: item.event_name || "",
          community_name: item.community_name || "",
          picture_file: item.picture_file || "",
        }));
      } else {
        this.homeEvents = [];
        this.alarmRecords = [];
      }

      if (Array.isArray(data.event_by_type_group)) {
        this.homeEventChartData = buildHomeEventChartDataFromGroup(
          data.event_by_type_group,
          flatEvents
        );
      } else {
        this.homeEventChartData = [];
      }
    },

    // 获取事件列表数据
    async fetchEventList() {
      try {
        // 不传参调用API
        const response = await dashboardAPI.getEventList();

        // 保存事件列表数据
        if (response.data || response) {
          const eventData = response.data;
          // 检查数据不为空且为对象格式（单条事件）
          if (
            eventData &&
            typeof eventData === "object" &&
            Object.keys(eventData).length > 0
          ) {
            this.eventList = eventData; // 包装成数组

            // 弹窗通知
            this.showEventNotification(eventData);
          } else {
            this.eventList = {};
          }
        }
      } catch (error) {
        console.error("事件列表数据获取失败:", error);
        this.eventList = [];
      }
    },

    // 显示事件通知弹窗
    showEventNotification(eventData) {
      // 构建消息内容
      const messageContent = `${eventData.community_name}电梯于${eventData.createtime}触发电梯${eventData.event_name}，请及时通知维保师傅到场救援并对乘客进行梯困安抚流程作业`;

      // 使用全局 $message 方法显示通知（美观的项目弹窗）
      this.$message.warning(messageContent, 5000);

      // 立即停止事件轮询
      this.stopEventPolling();

      // 5秒后跳转到homeTwo页面 - 根据事件中的社区名称查找对应项目
      this.jumpTimer = setTimeout(() => {
        // 从社区数据中查找匹配的项目
        const matchedProject = this.communityData.find(
          (item) => item.name === eventData.community_name
        );

        if (matchedProject) {
          this.$router.push({
            name: "homeTwo",
            params: { id: matchedProject.id },
            query: { name: matchedProject.name },
          });
        } else {
          // 如果没有找到匹配项目，只跳转页面
          this.$router.push({ name: "homeTwo" });
        }
      }, 5000);
    },

    // 停止事件轮询
    stopEventPolling() {
      if (this.eventPollingTimer) {
        clearInterval(this.eventPollingTimer);
        this.eventPollingTimer = null;
      }
    },

    // 更新时间
    startClock() {
      this.updateTime();
      this.timer = setInterval(() => {
        this.updateTime();
      }, 1000);
    },

    updateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      this.currentTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    // 定时刷新数据
    startDataRefresh() {
      // 原有的数据刷新（每60秒）
      this.dataRefreshTimer = setInterval(() => {
        this.fetchAllData();
      }, 60000);

      // 新增事件列表轮询（每10秒），保存到变量以便后续停止
      clearInterval(this.eventPollingTimer);
      this.eventPollingTimer = setInterval(() => {
        this.fetchEventList();
      }, 10000);
    },

    // 格式化日期为 YYYY-MM-DD
    formatDate(date) {
      return formatDateValue(date);
    },

    // 时间范围变化
    onDateRangeChange() {
      this.fetchAllData();
    },

    // 处理退出登录
    async handleLogout() {
      try {
        await logout();
        // 清除本地存储的token或其他认证信息
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        // 跳转到登录页
        this.$router.push("/login");
      } catch (error) {
        console.error("退出登录失败:", error);
        // 即使API调用失败也强制退出到登录页
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        this.$router.push("/login");
      }
    },
    // 处理图表类型切换
    handleChartTypeChange(type) {
      this.defaultChartType = type;
    },
  },
};
</script>

<style scoped lang="less">
.dashboard-container {
  background: linear-gradient(135deg, #0a1929 0%, #1a2332 50%, #0e2a42 100%);
  color: white;
  font-family: "Microsoft YaHei", sans-serif;
  min-height: 100vh;
  padding: 15px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.dashboard-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(circle at 20% 20%,
      rgba(0, 212, 255, 0.1) 0%,
      transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
  z-index: 1;
}

.dashboard-container::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: repeating-linear-gradient(90deg,
      transparent,
      transparent 98px,
      rgba(0, 212, 255, 0.03) 100px),
    repeating-linear-gradient(0deg,
      transparent,
      transparent 98px,
      rgba(0, 212, 255, 0.03) 100px);
  pointer-events: none;
  z-index: 1;
}

.dashboard-container>* {
  position: relative;
  // z-index: 2;
}

.header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.1), transparent);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  gap: 15px;
}

.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.2), transparent);
  animation: headerScan 3s infinite;
}

.header-left,
.header-right {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

/* 退出登录按钮样式 */
.logout-btn {
  background: linear-gradient(135deg, rgba(255, 51, 102, 0.8), rgba(204, 0, 51, 0.8));
  border: 1px solid rgba(255, 51, 102, 0.5);
  border-radius: 5px;
  padding: 6px 8px;
  color: white;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
}

.logout-btn svg {
  width: 16px;
  height: 16px;
}

.logout-btn:hover {
  background: linear-gradient(135deg, #ff3366, #cc0033);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(255, 51, 102, 0.4);
}

.logo-container {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.logo {
  height: 32px;
  width: auto;
  filter: drop-shadow(0 0 5px rgba(0, 212, 255, 0.5));
}

.header-left {
  .search-dropdown {
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 5px;
    padding: 8px;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      border-color: #00d4ff;
      box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
    }

    .search-input {
      background: transparent;
      border: none;
      outline: none;
      color: #fff;
      font-size: 12px;
      width: 160px;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }
    }

    .dropdown-icon {
      font-size: 10px;
      color: #00d4ff;
      margin-left: 5px;
    }

    .dropdown-list {
      position: absolute;
      top: 100%;
      left: 0;
      min-width: 300px;
      margin-top: 5px;
      background: rgba(10, 25, 41, 0.98);
      border: 1px solid rgba(0, 212, 255, 0.5);
      border-radius: 5px;
      max-height: 300px;
      overflow-y: auto;
      z-index: 9999;
      box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);

      .dropdown-item {
        padding: 10px 12px;
        font-size: 12px;
        color: #fff;
        cursor: pointer;
        transition: all 0.2s;
        border-bottom: 1px solid rgba(0, 212, 255, 0.1);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: rgba(0, 212, 255, 0.2);
          color: #00d4ff;
        }
      }
    }
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

@keyframes headerScan {
  0% {
    left: -100%;
  }

  100% {
    left: 100%;
  }
}

.title {
  font-size: 24px;
  font-weight: bold;
  color: #00d4ff;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.6);
  letter-spacing: 1px;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;
}

.title::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
}

.project-total,
.current-time {
  font-size: 14px;
  color: #ffffff;
  background: rgba(0, 212, 255, 0.1);
  padding: 6px 12px;
  border-radius: 5px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  position: relative;
  overflow: hidden;
}

.project-total::before,
.current-time::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
}

/* 时间检索组件样式 */
.time-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
}

.filter-label {
  font-size: 14px;
  color: #88c4ff;
  white-space: nowrap;
}

.date-range-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-picker {
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 4px;
  padding: 4px 8px;
  color: #ffffff;
  font-size: 12px;
  outline: none;
  transition: all 0.3s;
  min-width: 120px;
}

.date-picker:focus {
  border-color: #00d4ff;
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.3);
}

.date-picker::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
}

.date-separator {
  color: #00d4ff;
  font-size: 12px;
  font-weight: bold;
  margin: 0 1px;
}

.time::before,
.project-total::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
}

.main-content {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  height: 600px;
  align-items: stretch;
  /* 确保所有列等高 */
}

.left-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: 100%;
}

.stats-pie-container {
  display: flex;
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  height: 220px;
  /* 固定高度 */
  position: relative;
  overflow: hidden;

  :deep(.daily-event-analysis) {
    flex: 1;
    width: 100%;
    min-width: 0;
  }
}

.stats-pie-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
}

.chart-container {
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 0 15px 15px;
  box-sizing: border-box;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  flex: 1;
  min-height: 120px;
  /* 最小高度 */
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
}

.chart-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
}

.center-section {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.right-section {
  flex: 1;
  height: 100%;
  width: 30%;
}

.stat-details {
  width: 130px;
  padding-right: 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-item {
  margin-bottom: 8px;
  padding: 8px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 136, 204, 0.1));
  border-radius: 8px;
  border-left: 3px solid #00d4ff;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stat-item:last-child {
  margin-bottom: 0;
}

.stat-number {
  font-size: 22px;
  font-weight: bold;
  color: #00d4ff;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  line-height: 1;
}

.stat-desc {
  font-size: 11px;
  color: #88c4ff;
  margin-top: 3px;
  line-height: 1.2;
}

.pie-chart-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-title {
  font-size: 16px;
  margin-bottom: 10px;
  color: #00d4ff;
  text-align: center;
  font-weight: bold;
}

// 3D电梯模型样式
.elevator-3d {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1000px;
}

.elevator-svg-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(10, 25, 41, 0.3);
  // border-radius: 15px;
  // border: 1px solid rgba(0, 212, 255, 0.3);
  // box-shadow:
  //   0 0 30px rgba(0, 212, 255, 0.2),
  //   inset 0 0 30px rgba(0, 212, 255, 0.1);
}

.elevator-svg {
  width: 100%;
  height: 100%;
  // filter: drop-shadow(0 0 10px rgba(0, 212, 255, 0.5));
}

.bottom-section {
  display: flex;
  gap: 20px;
  height: 350px;
}

.line-chart-section {
  flex: 1;
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 0 15px 15px;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  box-sizing: border-box;
}

.alarm-records {
  flex: 1;
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
}

.record-header {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 2px solid rgba(0, 212, 255, 0.5);
  margin-bottom: 10px;
}

.header-item {
  font-weight: bold;
  color: #00d4ff;
  flex: 1;
  text-align: center;
}

.record-table {
  height: 270px;
  overflow-y: auto;
}

.record-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background-color 0.3s;
}

.record-row:hover {
  background-color: rgba(0, 212, 255, 0.1);
}

.record-time {
  color: #00d4ff;
  font-size: 12px;
  flex: 1;
  text-align: center;
}

.record-type {
  color: #ffaa00;
  font-size: 12px;
  flex: 1;
  text-align: center;
}

.record-location {
  color: #ffffff;
  font-size: 12px;
  flex: 2;
  text-align: center;
}

.platform-buttons {
  flex: 1;
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
}

.platform-title {
  font-size: 16px;
  margin-bottom: 15px;
  color: #00d4ff;
  text-align: center;
  font-weight: bold;
}

.button-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  height: 280px;
  overflow-y: auto;
}

.platform-button {
  background: linear-gradient(135deg, rgba(0, 136, 204, 0.8), rgba(0, 68, 102, 0.8));
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 5px;
  padding: 8px;
  color: white;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s;
  font-size: 12px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.platform-button:hover {
  background: linear-gradient(135deg, #00d4ff, #0088cc);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
  color: white;
  text-decoration: none;
}

// 滚动条样式
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 212, 255, 0.5);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 212, 255, 0.8);
}

.section {
  flex: 1;
}
</style>
